import type { RightsStatus } from "@/lib/api/api-client";

/**
 * Ce qu'une carte et une fiche disent des droits d'un morceau.
 *
 * # Le défaut qu'on répare
 *
 * La carte marquait « Draft » dès que `isPublic` était faux. Deux problèmes.
 * D'abord le mot : « brouillon » est un état d'éditeur — publié au catalogue ou
 * non — et il ne veut rien dire pour quelqu'un qui vient de déposer un fichier.
 * Ensuite le silence : un import de chanson sous droits et une transcription de
 * Bach saisie à la main portaient exactement la même marque, alors que ce qui
 * les sépare est précisément la question qu'on aimerait voir posée.
 *
 * # L'exception se marque, la règle non
 *
 * `public_domain` ne rend **aucune** marque. C'est le statut de tout le
 * catalogue : deux cent dix pastilles identiques n'apprennent rien et
 * chargeraient chaque tuile d'un bruit constant. On ne marque que ce qui sort
 * de l'ordinaire.
 *
 * # `unknown` n'est pas `restricted`, et c'est une contrainte d'honnêteté
 *
 * `restricted` **affirme** que le morceau est sous droits. `unknown` dit que
 * personne ne les a établis — c'est le défaut de tout import, et la personne
 * peut aussi bien avoir déposé sa propre composition qu'un Bach qu'elle a saisi
 * elle-même. Écrire « n'est pas libre de droits » sur un `unknown` affirmerait
 * ce qu'on ignore.
 *
 * Ce qu'`unknown` implique vraiment, et qui est utile à dire : le morceau reste
 * privé et ne peut pas être publié. C'est ce que porte l'infobulle, la pastille
 * se contentant de nommer l'état.
 *
 * # Deux `unknown`, et un seul mot sur la carte
 *
 * Depuis que les droits sont détectés à l'import
 * (`docs/spec/droits-a-l-import.md`), `unknown` recouvre deux situations : une
 * œuvre du domaine public dont la gravure n'est pas établie, et un morceau dont
 * on ne sait rien. La carte les dit du même mot, parce qu'elle n'a la place que
 * d'un mot et que la conséquence est la même. La **fiche**, elle, les
 * distingue : `rightsNote` explique le verdict et sur quelles dates il
 * s'appuie, ce qui permet à un humain de le refaire.
 *
 * Module pur : ni React, ni DOM. Testé dans `__tests__/piece-rights.test.ts`.
 */

/** Ce qu'une carte peut porter : un mot, et de quoi l'expliquer au survol. */
export type PieceRightsMark = {
  readonly label: string;
  /** La conséquence, en une phrase. Sert d'infobulle et de nom accessible. */
  readonly title: string;
};

/**
 * Ce que les deux fonctions lisent d'une pièce.
 *
 * Décrit ici plutôt que par un `Pick` : `rightsNote` y reste facultatif, ce qui
 * laisse ces fonctions utilisables depuis un appelant qui ne tient qu'une carte
 * partielle. L'API le sert désormais partout, mais dépendre de sa présence
 * n'apporterait rien et fermerait la porte au premier écran qui ne l'a pas.
 */
type RightsFields = {
  readonly rightsStatus: RightsStatus;
  readonly license: string | null;
  readonly rightsNote?: string | null;
};

/**
 * Longueur au-delà de laquelle une licence ne tient plus dans un coin de tuile.
 *
 * « CC0 1.0 » et « Public Domain Mark 1.0 » passent ; « CC0 1.0 (gravure) ;
 * œuvre sous droits » ne passe pas, et une pastille tronquée sur une question
 * de droits est pire qu'une pastille générique. Au-delà, la pastille nomme le
 * statut et la licence entière part dans l'infobulle.
 */
const BADGE_MAX_LENGTH = 22;

const STATUS_LABELS: Record<RightsStatus, string> = {
  unknown: "Droits non établis",
  public_domain: "Domaine public",
  licensed: "Droits accordés",
  restricted: "Sous droits",
};

/**
 * Ce que le statut change **pour la personne**, en une clause courte.
 *
 * # Ce qu'on a arrêté de dire
 *
 * « Il ne peut pas être publié au catalogue. » C'était vrai et sans intérêt :
 * personne qui dépose un MIDI ne cherchait à publier quoi que ce soit, et
 * répondre à une question que personne ne pose allonge la phrase sans
 * l'éclairer. La publication est un souci d'éditeur, comme « Draft » l'était.
 *
 * # Ce qui reste
 *
 * Où va le morceau. C'est la seule chose qui change quelque chose, et elle est
 * rassurante plutôt qu'inquiétante : il est à toi, tu le gardes.
 */
const STATUS_CONSEQUENCE: Record<RightsStatus, string> = {
  unknown: "Il reste dans ta bibliothèque, à toi seul.",
  // La règle de tout le catalogue : elle ne se commente pas.
  public_domain: "",
  licensed: "Les droits ont été obtenus pour Virtuosa.",
  restricted: "Il reste dans ta bibliothèque, à toi seul.",
};

/**
 * La marque à poser sur une carte, `null` quand il n'y a rien d'exceptionnel à
 * dire.
 */
export function pieceRightsMark(piece: RightsFields): PieceRightsMark | null {
  const status = piece.rightsStatus;

  if (status === "public_domain") {
    return null;
  }

  const license = cleanLicense(piece.license);
  const statusLabel = STATUS_LABELS[status];

  /*
    L'infobulle dit ce que la pastille ne dit pas, et rien d'autre.

    Elle répétait la pastille — « Sous droits » suivi de « Ce morceau est sous
    droits » — puis recopiait la licence déjà affichée juste à côté. Trois fois
    la même information sur deux lignes. On assemble donc à partir de morceaux
    qui ne se recouvrent pas : le motif du verdict, puis où va le morceau.
  */
  const title = joinSentences([cleanRightsNote(piece.rightsNote), STATUS_CONSEQUENCE[status]]);

  /*
    La licence prend la pastille quand elle y tient : « CC BY 4.0 » dit plus que
    « Droits accordés », et c'est le crédit qu'on doit de toute façon rendre.
    Sinon elle passe dans l'infobulle, où la longueur ne coûte rien.
  */
  if (license !== null && license.length <= BADGE_MAX_LENGTH) {
    return { label: license, title: title ?? statusLabel };
  }

  return {
    label: statusLabel,
    title: joinSentences([license, title]) ?? statusLabel,
  };
}

/**
 * Assemble des fragments en un paragraphe, en jetant les vides.
 *
 * Existe parce que chaque morceau est facultatif — une pièce peut n'avoir ni
 * note, ni licence, ni conséquence à énoncer — et que la concaténation naïve
 * produisait des « . » orphelins et des doubles espaces sur les cas creux.
 */
function joinSentences(parts: readonly (string | null | undefined)[]): string | null {
  const kept = parts
    .map((part) => part?.trim() ?? "")
    .filter((part) => part !== "")
    .map((part) => (/[.!?]$/.test(part) ? part : `${part}.`));

  return kept.length === 0 ? null : kept.join(" ");
}

/**
 * Le paragraphe de droits d'une **fiche** : ce que le verdict a conclu, puis ce
 * que ça change.
 *
 * C'est ici, et nulle part ailleurs, que les deux `unknown` se distinguent —
 * « Œuvre du domaine public : Chopin est mort en 1849 […]. Gravure non
 * établie. » n'est pas la même chose que le silence. La note vient du serveur,
 * qui est le seul à connaître les dates sur lesquelles il a tranché.
 *
 * `null` quand il n'y a rien à dire, c'est-à-dire sur une pièce du domaine
 * public établie, qui est la règle de tout le catalogue.
 */
export function pieceRightsDetail(piece: RightsFields): string | null {
  /*
    Ni le statut ni la licence : `pieceRightsLine()` les affiche juste au-dessus.

    C'est le défaut qu'on répare. La fiche rendait « Sous droits · CC0 1.0 »,
    puis, en dessous, « CC0 1.0. Ce morceau est sous droits. Il reste privé et ne
    peut pas être publié au catalogue. » Deux lignes pour trois fois la même
    chose. Ce paragraphe ne porte donc que ce que la ligne ne peut pas dire : le
    motif du verdict, et où va le morceau.
  */
  return joinSentences([
    cleanRightsNote(piece.rightsNote),
    STATUS_CONSEQUENCE[piece.rightsStatus],
  ]);
}

/**
 * La ligne de droits d'une **fiche**, où il y a la place d'être précis.
 *
 * Une partition porte deux couches — l'œuvre et la gravure — et la fiche est le
 * seul écran assez large pour les distinguer. Elle rend donc la licence en
 * clair *et* le statut, là où la carte doit choisir.
 *
 * `null` sur `public_domain` sans licence relevée : il n'y aurait qu'une
 * redite du bas de la carte du répertoire, qui porte déjà l'attribution PDMX.
 */
export function pieceRightsLine(piece: RightsFields): string | null {
  const license = cleanLicense(piece.license);
  const statusLabel = STATUS_LABELS[piece.rightsStatus];

  if (license === null) {
    return piece.rightsStatus === "public_domain" ? null : statusLabel;
  }

  return `${statusLabel} · ${license}`;
}

/**
 * Nettoie une chaîne de licence.
 *
 * C'est de la **donnée**, relevée à l'import, jamais rédigée : elle a porté des
 * tirets cadratins, qui sont bannis du texte affiché du produit. On ne se fie
 * donc pas à sa propreté, ici ni ailleurs — la corriger à la source ne règle
 * que les lignes déjà en base, pas le prochain lot importé.
 *
 * `null` sur une chaîne vide : une licence blanche n'est pas une licence.
 */
export function cleanLicense(license: string | null): string | null {
  // Le point-virgule : c'est ce que la chaîne veut dire quand elle sépare les
  // deux couches de droits d'une même partition.
  return normalize(license, " ; ");
}

/**
 * Même précaution sur la note de droits, avec une virgule : dans une phrase,
 * un tiret long est une incise, et la virgule la rend sans la déformer.
 *
 * `undefined` autant que `null` : le champ est facultatif côté API.
 */
export function cleanRightsNote(note: string | null | undefined): string | null {
  return normalize(note ?? null, ", ");
}

function normalize(value: string | null, dashReplacement: string): string | null {
  if (value === null) {
    return null;
  }

  const cleaned = value
    .replace(/\s*[—–]\s*/g, dashReplacement)
    .replace(/\s+/g, " ")
    .trim();

  return cleaned === "" ? null : cleaned;
}

/**
 * Ce que le retour d'action dit après avoir copié un lien de partage.
 *
 * # Deux phrases, et la seconde n'apparaît pas toujours
 *
 * La première dit ce que le lien autorise, ce que la personne ne sait pas. La
 * seconde n'apparaît que sur une œuvre protégée, et elle **oriente** au lieu
 * d'interdire : le partage reste ouvert sur ces morceaux, parce qu'une fonction
 * qui ne marcherait que sur le domaine public ne servirait à personne. Ce qu'on
 * peut faire, c'est rappeler au bon moment ce à quoi elle est destinée.
 *
 * # Seulement sur `restricted`
 *
 * Pas sur `unknown`. Un import dont personne n'a établi les droits peut être la
 * composition de la personne elle-même : lui dire « c'est sous droits » serait
 * affirmer ce qu'on ignore, et le retour d'action est le pire endroit pour une
 * affirmation qu'on ne peut pas nuancer.
 */
export function shareLinkDetail(piece: RightsFields): string {
  const base = "Toute personne avec ce lien pourra ouvrir ce morceau.";

  return piece.rightsStatus === "restricted"
    ? `${base} Il est sous droits : garde ce lien pour tes proches.`
    : base;
}
